<template>
  <div>
    <PageHeader
      title="Data"
      subtitle="Ingestion, feeds, and imports"
    >
      <template #bottom>
        <Page_Tabs
          class="px-6 pt-4"
          :initial-tab="selected_tab"
          :tabs="etl_tabs"
          no-border
          @select="switch_tab"
        />
      </template>
    </PageHeader>

    <!-- Panes mount on first visit and stay alive (v-show) so tab switches
         never destroy in-progress state (uploads, classification jobs, edits). -->
    <div class="p-6">
      <div v-if="visited.has('Feeds')" v-show="selected_tab === 'Feeds'">
        <Overview_ETL_Feed_Registry />
      </div>
      <div v-if="visited.has('Catalog')" v-show="selected_tab === 'Catalog'">
        <Overview_ETL_Data_Catalog />
      </div>
      <div v-if="visited.has('Ingestion')" v-show="selected_tab === 'Ingestion'">
        <Overview_ETL_Ingestion @open-agent="openAgent" />
      </div>
      <div v-if="visited.has('Agent')" v-show="selected_tab === 'Agent'">
        <Overview_ETL_Agent ref="agent" />
      </div>
      <div v-if="visited.has('MDR')" v-show="selected_tab === 'MDR'">
        <MDR />
      </div>
      <div v-if="visited.has('Reconciliation')" v-show="selected_tab === 'Reconciliation'">
        <Overview_ETL_Reconciliation />
      </div>
      <div v-if="visited.has('DataSources')" v-show="selected_tab === 'DataSources'">
        <Overview_ETL_Data_Sources />
      </div>
      <div v-if="visited.has('DataAttributes')" v-show="selected_tab === 'DataAttributes'">
        <Overview_ETL_Data_Attributes />
      </div>
      <div v-if="visited.has('Workflows')" v-show="selected_tab === 'Workflows'">
        <Overview_ETL_Workflows ref="workflows" />
      </div>
      <div v-if="visited.has('Workflow Builder')" v-show="selected_tab === 'Workflow Builder'">
        <Overview_ETL_Workflow_Builder ref="builder" />
      </div>
      <div v-if="visited.has('Uploads')" v-show="selected_tab === 'Uploads'">
        <Customer_File_Upload />
      </div>
    </div>
  </div>
</template>

<script>
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Overview_ETL_Feed_Registry from '@/components/ETL_Utility/Overview_ETL_Feed_Registry.vue';
import Overview_ETL_Data_Catalog from '@/components/ETL_Utility/Overview_ETL_Data_Catalog.vue';
import Overview_ETL_Ingestion from '@/components/ETL_Utility/Ingestion/Overview_ETL_Ingestion.vue';
import Overview_ETL_Agent from '@/components/ETL_Utility/Overview_ETL_Agent.vue';
import MDR from '@/components/ETL_Utility/mdr/MDR.vue';
import Overview_ETL_Reconciliation from '@/components/ETL_Utility/Reconciliation/Overview_ETL_Reconciliation.vue';
import Overview_ETL_Workflow_Builder from '@/components/ETL_Utility/Overview_ETL_Workflow_Builder.vue';
import Overview_ETL_Workflows from '@/components/ETL_Utility/Overview_ETL_Workflows.vue';
import Overview_ETL_Data_Sources from '@/components/ETL_Utility/Overview_ETL_Data_Sources.vue';
import Overview_ETL_Data_Attributes from '@/components/ETL_Utility/Overview_ETL_Data_Attributes.vue';
import Customer_File_Upload from '@/components/DataUpload/Customer_File_Upload.vue';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import { DATA_DETAIL_TABS, dataTabLabel, dataTabLocation, dataTabSegment } from '../../../router/data_routes';

export default {
  name: 'OverviewListETLUtility',
  components: {
    Page_Tabs,
    PageHeader,
    Overview_ETL_Feed_Registry,
    Overview_ETL_Ingestion,
    Overview_ETL_Agent,
    MDR,
    Overview_ETL_Reconciliation,
    Overview_ETL_Workflow_Builder,
    Overview_ETL_Workflows,
    Overview_ETL_Data_Sources,
    Overview_ETL_Data_Attributes,
    Customer_File_Upload,
    Overview_ETL_Data_Catalog,
  },
  data() {
    return {
      selected_tab: null,
      visited: new Set(),
    };
  },
  computed: {
    etl_tabs() {
      const tabs = [];
      if (canAccessFeature(FEATURES.MODULE_DATA_INGESTION)) {
        tabs.push({ label: 'Agent', icon: ['fas', 'wand-magic-sparkles'] });
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_FEED_REGISTRY)) {
        tabs.push('Feeds');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_CATALOG)) {
        tabs.push('Catalog');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_INGESTION)) {
        tabs.push('Ingestion');
      }
      // Model Import is no longer a tab — it's a modal launched from Uploads
      // (gated there by MODULE_DATA_MODEL_IMPORT).
      if (canAccessFeature(FEATURES.MODULE_DATA_MDR)) {
        tabs.push('MDR');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_RECONCILIATION)) {
        tabs.push('Reconciliation');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_WORKFLOW_BUILDER)) {
        tabs.push('DataSources', 'DataAttributes', 'Workflows', 'Workflow Builder');
      }
      if (
        canAccessFeature(FEATURES.ACTION_DATA_UPLOAD)
        || canAccessFeature(FEATURES.MODULE_DATA_MODEL_IMPORT)
      ) {
        tabs.push('Uploads');
      }
      return tabs;
    },
  },
  watch: {
    // The open tab comes from the path (/data/:tab, /data/data-attributes/:id), so
    // links are shareable and Back/Forward move between tabs. Bare /data, or a tab
    // the user can't open, is replaced with the remembered tab (or the first one).
    $route: { handler: 'syncTabFromRoute', immediate: true },
    etl_tabs: 'syncTabFromRoute',
  },
  methods: {
    syncTabFromRoute() {
      const labels = this.etl_tabs.map((t) => (typeof t === 'string' ? t : t.label));
      if (!labels.length || !this.$route.path.startsWith('/data')) return;
      const routed = DATA_DETAIL_TABS[this.$route.name] || dataTabLabel(this.$route.params.tab);
      if (routed && labels.includes(routed)) {
        this.activateTab(routed);
        return;
      }
      const stored = dataTabLabel(dataTabSegment(localStorage.getItem('etl_utility_tab')));
      this.$router.replace(dataTabLocation(labels.includes(stored) ? stored : labels[0]));
    },
    activateTab(tab) {
      if (this.selected_tab === tab) return;
      const revisit = this.visited.has(tab);
      this.selected_tab = tab;
      this.visited = new Set([...this.visited, tab]);
      localStorage.setItem('etl_utility_tab', tab);
      if (revisit && tab === 'Workflows' && this.$refs.workflows) this.$refs.workflows.load();
      if (revisit && tab === 'Workflow Builder' && this.$refs.builder) this.$refs.builder.refreshWorkflows();
    },
    // Tab clicks and in-page hand-offs navigate; the route watcher activates the pane.
    async switch_tab(tab) {
      if (tab !== this.selected_tab) await this.$router.push(dataTabLocation(tab));
      await this.$nextTick();
    },
    // Panes link to each other with real URLs (see router/data_routes.js); only
    // the Agent hand-off, which opens a separate page, still goes through here.
    async openAgent({ s3Key = '' } = {}) {
      await this.switch_tab('Agent');
      this.$nextTick(() => {
        if (this.$refs.agent && this.$refs.agent.openFile) this.$refs.agent.openFile(s3Key);
      });
    },
  },
};
</script>
